import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import keycloak from '@/keycloak'
import { axios } from '@/core/classes/HttpClient'
import ReAuthDialog from '@/components/auth/ReAuthDialog'
import { syncService } from '@/core/offline'
import { refreshKeycloakToken } from '@/utils/auth-token'

interface AuthContextType {
  isAuthenticated: boolean
  isReAuthDialogOpen: boolean
  openReAuthDialog: () => void
  closeReAuthDialog: () => void
  handleAuthError: (error: Error, retryCallback?: () => void) => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const [isReAuthDialogOpen, setIsReAuthDialogOpen] = useState(false)
  const [pendingRetryCallback, setPendingRetryCallback] = useState<(() => void) | undefined>()
  const [isInReAuthFlow, setIsInReAuthFlow] = useState(false)

  // Check authentication status
  const isAuthenticated = !!(keycloak.token && !keycloak.isTokenExpired())

  const openReAuthDialog = useCallback(() => {
    setIsReAuthDialogOpen(true)
    setIsInReAuthFlow(true)
  }, [])

  const closeReAuthDialog = useCallback(() => {
    setIsReAuthDialogOpen(false)
    setPendingRetryCallback(undefined)
    setIsInReAuthFlow(false)
  }, [])

  const authEventStateRef = useRef({
    closeReAuthDialog,
    enqueueSnackbar,
    isInReAuthFlow,
    isReAuthDialogOpen,
    pendingRetryCallback,
    t,
  })

  const handleAuthError = useCallback((error: Error, retryCallback?: () => void) => {
    if (error.message.includes('Authentication expired during sync operation')) {
      console.log('Authentication error detected during sync, showing re-auth dialog')
      enqueueSnackbar(t('auth.sessionExpiredSync', 'Session expired during sync'), { variant: 'warning' })
      setPendingRetryCallback(() => retryCallback)
      openReAuthDialog()
    } else {
      // For other auth errors, re-throw
      throw error
    }
  }, [openReAuthDialog, enqueueSnackbar, t])

  useEffect(() => {
    authEventStateRef.current = {
      closeReAuthDialog,
      enqueueSnackbar,
      isInReAuthFlow,
      isReAuthDialogOpen,
      pendingRetryCallback,
      t,
    }
  }, [closeReAuthDialog, enqueueSnackbar, isInReAuthFlow, isReAuthDialogOpen, pendingRetryCallback, t])

  // Listen for Keycloak authentication events
  useEffect(() => {
    const previousOnAuthSuccess = keycloak.onAuthSuccess
    const previousOnAuthError = keycloak.onAuthError
    const previousOnTokenExpired = keycloak.onTokenExpired
    const previousOnAuthLogout = keycloak.onAuthLogout

    const handleAuthSuccess = () => {
      previousOnAuthSuccess?.()

      const { closeReAuthDialog, enqueueSnackbar, isInReAuthFlow, isReAuthDialogOpen, pendingRetryCallback, t } = authEventStateRef.current

      console.log('Keycloak authentication successful, isInReAuthFlow:', isInReAuthFlow)
      // Ensure axios carries the fresh token immediately after login
      if (keycloak.token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${keycloak.token}`
      }
      if (isInReAuthFlow && isReAuthDialogOpen) {
        console.log('Re-authentication completed, executing retry callback')
        enqueueSnackbar(t('auth.reAuthenticated', 'Authentication completed'), { variant: 'success' })

        if (pendingRetryCallback) {
          // Execute the retry callback after a short delay
          setTimeout(() => {
            pendingRetryCallback()
          }, 500)
        }

        closeReAuthDialog()
      } else {
        console.log('Normal authentication success, not in re-auth flow')
        // Reset re-auth state for normal login/logout flows
        setIsInReAuthFlow(false)
        setPendingRetryCallback(undefined)
      }
    }

    const handleAuthLogout = () => {
      previousOnAuthLogout?.()

      console.log('Keycloak logout successful, resetting re-auth state')
      // Reset all re-auth state when user logs out
      setIsReAuthDialogOpen(false)
      setIsInReAuthFlow(false)
      setPendingRetryCallback(undefined)
    }

    const handleAuthError = () => {
      previousOnAuthError?.()

      const { enqueueSnackbar, isInReAuthFlow, isReAuthDialogOpen, t } = authEventStateRef.current

      console.error('Keycloak authentication error')
      if (isInReAuthFlow && isReAuthDialogOpen) {
        enqueueSnackbar(t('auth.authFailed', 'Authentication failed'), { variant: 'error' })
      }
    }

    const handleTokenExpired = () => {
      console.log('Keycloak token expired')
      refreshKeycloakToken()
        .then((token) => {
          if (token) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
          }
        })
        .catch((error) => {
          console.error('Failed to refresh Keycloak token', error)
          previousOnTokenExpired?.()

          const { enqueueSnackbar, isInReAuthFlow, t } = authEventStateRef.current
          if (!isInReAuthFlow) {
            enqueueSnackbar(t('auth.sessionExpired', 'Your session has expired'), { variant: 'warning' })
          }
        })
    }

    keycloak.onAuthSuccess = handleAuthSuccess
    keycloak.onAuthError = handleAuthError
    keycloak.onTokenExpired = handleTokenExpired
    keycloak.onAuthLogout = handleAuthLogout

    return () => {
      if (keycloak.onAuthSuccess === handleAuthSuccess) keycloak.onAuthSuccess = previousOnAuthSuccess
      if (keycloak.onAuthError === handleAuthError) keycloak.onAuthError = previousOnAuthError
      if (keycloak.onTokenExpired === handleTokenExpired) keycloak.onTokenExpired = previousOnTokenExpired
      if (keycloak.onAuthLogout === handleAuthLogout) keycloak.onAuthLogout = previousOnAuthLogout
    }
  }, [])

  // Listen for sync authentication errors
  useEffect(() => {
    const handleSyncAuthError = (retryCallback: () => Promise<void>) => {
      console.log('Sync authentication error detected, opening re-auth dialog')
      setPendingRetryCallback(() => retryCallback)
      openReAuthDialog()
    }

    // Subscribe to sync auth errors
    const unsubscribe = syncService.onAuthError(handleSyncAuthError)

    return () => {
      if (typeof unsubscribe === 'function') unsubscribe()
    }
  }, [openReAuthDialog])

  const contextValue: AuthContextType = {
    isAuthenticated,
    isReAuthDialogOpen,
    openReAuthDialog,
    closeReAuthDialog,
    handleAuthError,
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
      <ReAuthDialog
        open={isReAuthDialogOpen}
        onClose={closeReAuthDialog}
        title={t('auth.syncSessionExpired', 'Sync Requires Re-authentication')}
        message={t('auth.syncSessionExpiredMessage',
          'Your authentication session has expired while trying to sync your offline data. Choose how you would like to proceed:')}
      />
    </AuthContext.Provider>
  )
}

export default AuthProvider
